import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { 
  ShoppingBag, 
  FileText, 
  MessageSquare, 
  Printer, 
  User, 
  LogOut,
  Receipt,
  Package,
  CreditCard
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GeneralInfoForm } from '@/components/account/GeneralInfoForm';
import { SecurityForm } from '@/components/account/SecurityForm';
import { DeliveryAddresses } from '@/components/account/DeliveryAddresses';
import MachinesTable from '@/components/account/MachinesTable';
import OrdersList from '@/components/account/OrdersList';
import { InvoicesList } from '@/components/account/InvoicesList';
import { DeliveriesList } from '@/components/account/DeliveriesList';
import { PaymentsList } from '@/components/account/PaymentsList';
import ComplaintsList from '@/components/account/ComplaintsList';
import ComplaintsForm from '@/components/account/ComplaintsForm';
import ComplaintDetail from '@/components/account/ComplaintDetail';

const Account = () => {
  const { partner, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['orders', 'invoices', 'deliveries', 'payments', 'claims', 'machines', 'profile'].includes(tab || '') ? tab : 'orders';
  });
  
  // State for complaints management
  const [complaintsView, setComplaintsView] = useState<'list' | 'form' | 'detail'>('list');
  const [selectedComplaint, setSelectedComplaint] = useState<any>(null);

  // Update tab when URL params change
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (['orders', 'invoices', 'deliveries', 'payments', 'claims', 'machines', 'profile'].includes(tab || '')) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Vous devez être connecté pour accéder à cette page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabsData = [
    { id: 'orders', label: 'Mes Commandes', icon: ShoppingBag },
    { id: 'invoices', label: 'Mes Factures', icon: Receipt },
    { id: 'deliveries', label: 'Mes Livraisons', icon: Package },
    { id: 'payments', label: 'Mes Paiements', icon: CreditCard },
    { id: 'claims', label: 'Mes Réclamations', icon: MessageSquare },
    { id: 'machines', label: 'Mes Machines', icon: Printer },
    { id: 'profile', label: 'Mon Profil', icon: User },
  ];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex gap-8">
        {/* Account Navigation Sidebar */}
        <div className="w-80 space-y-6">
          {/* User Info */}
          <Card>
            <CardContent className="flex flex-col items-center text-center p-6">
              <Avatar className="h-20 w-20 mb-4">
                <AvatarImage src={partner.avatar} />
                <AvatarFallback className="text-lg">
                  {getInitials(partner.name || 'User')}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-semibold mb-1">{partner.name}</h2>
              <p className="text-muted-foreground text-sm">{partner.email}</p>
            </CardContent>
          </Card>

          {/* Navigation Menu */}
          <Card>
            <CardContent className="p-4">
              <nav className="space-y-2">
                {tabsData.map((tab) => (
                  <Button
                    key={tab.id}
                    variant={activeTab === tab.id ? "default" : "ghost"}
                    onClick={() => setActiveTab(tab.id)}
                    className="w-full justify-start"
                  >
                    <tab.icon className="h-5 w-5 mr-3" />
                    <span>{tab.label}</span>
                  </Button>
                ))}
              </nav>
            </CardContent>
          </Card>

          {/* Sign Out Button */}
          <Card>
            <CardContent className="p-4">
              <Button variant="outline" onClick={handleLogout} className="w-full">
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsContent value="orders">
              <OrdersList />
            </TabsContent>

            <TabsContent value="invoices">
              <InvoicesList />
            </TabsContent>

            <TabsContent value="deliveries">
              <DeliveriesList />
            </TabsContent>

            <TabsContent value="payments">
              <PaymentsList />
            </TabsContent>

            <TabsContent value="claims">
              {complaintsView === 'list' && (
                <ComplaintsList 
                  onCreateNew={() => setComplaintsView('form')}
                  onViewDetail={(complaint) => {
                    setSelectedComplaint(complaint);
                    setComplaintsView('detail');
                  }}
                />
              )}
              {complaintsView === 'form' && (
                <ComplaintsForm 
                  onBack={() => setComplaintsView('list')}
                  onSubmit={() => setComplaintsView('list')}
                />
              )}
              {complaintsView === 'detail' && selectedComplaint && (
                <ComplaintDetail 
                  complaint={selectedComplaint}
                  onBack={() => setComplaintsView('list')}
                />
              )}
            </TabsContent>

            <TabsContent value="machines">
              <MachinesTable />
            </TabsContent>

            <TabsContent value="profile">
              <Card>
                <CardHeader>
                  <CardTitle>Mon Profil</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="general" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="general">Informations générales</TabsTrigger>
                      <TabsTrigger value="security">Sécurité</TabsTrigger>
                      <TabsTrigger value="addresses">Adresses de livraison</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="general" className="mt-6">
                      <GeneralInfoForm />
                    </TabsContent>
                    
                    <TabsContent value="security" className="mt-6">
                      <SecurityForm />
                    </TabsContent>
                    
                    <TabsContent value="addresses" className="mt-6">
                      <DeliveryAddresses />
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Account;